import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { tenantAssetService } from "../api/services/tenantAssetService";
import { TenantAssetResult } from "../schemas/tenantAssetSchema";

export const useTenantAssets = (
  tenantId?: string,
  assetType?: "logo" | "background" | "widget" | "report_logo"
) => {
  const queryClient = useQueryClient();

  // Fetch all assets for the current tenant
  const {
    data: allAssetsResult,
    isLoading: isLoadingAll,
    refetch: refetchAll,
  } = useQuery<TenantAssetResult, Error>({
    queryKey: ["my-tenant-assets"],
    queryFn: () => tenantAssetService.getMyAssets(),
  });

  // Filter assets by type if specified
  const assets = assetType
    ? (allAssetsResult?.assets || []).filter(
        (asset) => asset.assetType === assetType
      )
    : allAssetsResult?.assets || [];

  const isLoading = isLoadingAll;
  const refetch = refetchAll;

  const updateAssetMutation = useMutation({
    mutationFn: tenantAssetService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["my-tenant-assets"],
      });
      toast.success("Asset updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update asset");
    },
  });

  const createAssetMutation = useMutation({
    mutationFn: tenantAssetService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["my-tenant-assets"],
      });
      toast.success("Asset created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create asset");
    },
  });

  const deleteAssetMutation = useMutation({
    mutationFn: tenantAssetService.remove,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["my-tenant-assets"],
      });
      toast.success("Asset deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete asset");
    },
  });

  return {
    assets,
    isLoading,
    refetch,
    createAsset: createAssetMutation.mutateAsync,
    updateAsset: updateAssetMutation.mutateAsync,
    deleteAsset: deleteAssetMutation.mutateAsync,
    isCreating: createAssetMutation.isPending,
    isUpdating: updateAssetMutation.isPending,
    isDeleting: deleteAssetMutation.isPending,
  };
};
